<template>
  <div class="rs-listing">
    <RSParsha
      v-if="fetchedParshot.length"
      :fetchedParshot="fetchedParshot"
      :selectedParsha="selectedParsha"
      @selected-parsha-change="onSelectedParshaChange"
    />
    <RSFilters
      :fetchedYears="fetchedYears"
      :fetchedSeries="fetchedSeries"
      :fetchedCategories="fetchedCategories"
      :selectedYears="selectedYears"
      :selectedSeries="selectedSeries"
      :selectedCategories="selectedCategories"
      @selected-filters-change="onSelectedFiltersChange"
      :searchKeywords="searchKeywords"
      @search-keywords-change="onSearchKeywordsChange"
      :fetchedLanguages="fetchedLanguages"
      :selectedLanguage="selectedLanguage"
      @selected-language-change="onSelectedLanguageChange"
      :fetchedResults="fetchedResults"
    />
    <RSResults
      :fetchedResults="fetchedResults"
      :baseURL="baseURL"
      @bottom-card-change="onBottomCardChange"
      @scroll-to-top-request="onScrollToTopRequest"
      :selectedLanguage="selectedLanguage"
    />
  </div>
</template>

<script>
import RSParsha from "./RSCommentaryListingParsha.vue";
import RSFilters from "./RSCommentaryListingFilters.vue";
import RSResults from "./RSCommentaryListingResults.vue";

export default {
  name: "RSCommentaryListing",
  components: {
    RSParsha,
    RSFilters,
    RSResults,
  },
  props: {
    baseURL: String,
  },
  data: function () {
    return {
      fetchedResults: {},
      fetchedParshot: [],
      fetchedYears: [],
      fetchedSeries: [],
      fetchedCategories: [],
      fetchedLanguages: [],
      selectedParsha: "",
      selectedYears: [],
      selectedSeries: [],
      selectedCategories: [],
      selectedLanguage: "",
      searchKeywords: "",
      fetchResultsAbortController: undefined,
      isLoading: false,
      maxDisplayedPage: 1,
      maxLoadedPage: 1,
      reachedLastPage: false,
      itemsToLoadPerPage: 12,
      bottomCardObserver: undefined,
    };
  },
  watch: {
    $route(to, from) {
      this.onRouteUpdated();
    },
  },
  mounted: function () {
    let refToThis = this;
    this.fetchFilters().then(() => {
      refToThis.onRouteUpdated();
    });
  },
  methods: {
    fetchFilters() {
      let endpoint = "/wp-json/rs/v1/commentary-filters/";
      if (this.fetchResultsAbortController)
        this.fetchResultsAbortController.abort();
      this.isLoading = true;
      return fetch(endpoint)
        .then((response) => response.json())
        .then((data) => {
          this.fetchedParshot = data.filters.rs_cc_parsha;
          this.fetchedYears = data.filters.rs_cc_year.sort().reverse();
          this.fetchedSeries = data.filters.rs_cc_series;
          this.fetchedCategories = data.filters.category;
          this.fetchedLanguages = data.filters.languages;
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    fetchResults() {
      let endpoint = "/wp-json/rs/v1/commentary/";
      let queryString = [];
      if (this.selectedParsha && this.selectedParsha.length)
        queryString.push("rs_cc_parsha=" + this.selectedParsha);
      if (this.selectedYears && this.selectedYears.length)
        queryString.push("rs_cc_year=" + this.selectedYears.join(","));
      if (this.selectedSeries && this.selectedSeries.length)
        queryString.push("rs_cc_series=" + this.selectedSeries.join(","));
      if (this.selectedCategories && this.selectedCategories.length)
        queryString.push("categories=" + this.selectedCategories.join(","));
      if (this.searchKeywords && this.searchKeywords.length)
        queryString.push("keywords=" + encodeURIComponent(this.searchKeywords));
      if (this.selectedLanguage && this.selectedLanguage.length)
        queryString.push("lang=" + encodeURIComponent(this.selectedLanguage));

      queryString.forEach((element, i) => {
        if (i === 0) endpoint += "?" + element;
        else endpoint += "&" + element;
      });

      if (this.fetchResultsAbortController)
        this.fetchResultsAbortController.abort();
      this.fetchResultsAbortController = new AbortController();
      let signal = this.fetchResultsAbortController.signal;
      this.isLoading = true;

      fetch(endpoint, { signal })
        .then((response) => response.json())
        .then((data) => {
          this.isLoading = false;
          this.maxDisplayedPage = 1;
          this.maxLoadedPage = 1;
          this.fetchedResults = data;
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.log(error);
          }
        });
    },
    fetchMoreResults() {
      if (
        this.maxDisplayedPage < this.fetchedResults["total_pages"] &&
        this.maxDisplayedPage === this.maxLoadedPage
      ) {
        let endpoint = "/wp-json/rs/v1/commentary/";
        let queryString = [];
        if (this.selectedParsha && this.selectedParsha.length)
          queryString.push("rs_cc_parsha=" + this.selectedParsha);
        if (this.selectedYears && this.selectedYears.length)
          queryString.push("rs_cc_year=" + this.selectedYears.join(","));
        if (this.selectedSeries && this.selectedSeries.length)
          queryString.push("rs_cc_series=" + this.selectedSeries.join(","));
        if (this.selectedCategories && this.selectedCategories.length)
          queryString.push("categories=" + this.selectedCategories.join(","));
        if (this.searchKeywords && this.searchKeywords.length)
          queryString.push("keywords=" + encodeURIComponent(this.searchKeywords));
        if (this.selectedLanguage && this.selectedLanguage.length)
          queryString.push("lang=" + encodeURIComponent(this.selectedLanguage));

        queryString.push("p=" + ++this.maxLoadedPage);

        queryString.forEach((element, i) => {
          if (i === 0) endpoint += "?" + element;
          else endpoint += "&" + element;
        });

        if (this.fetchResultsAbortController)
          this.fetchResultsAbortController.abort();
        this.fetchResultsAbortController = new AbortController();
        let signal = this.fetchResultsAbortController.signal;
        this.isLoading = true;

        fetch(endpoint, { signal })
          .then((response) => response.json())
          .then((data) => {
            this.isLoading = false;
            this.maxDisplayedPage = data["current_page"];
            this.fetchedResults["posts"].push(...data["posts"]);
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.log(error);
            }
          });
      }
    },
    onBottomCardChange(newBottomCard) {
      if (this.bottomCardObserver) this.bottomCardObserver.disconnect();
      this.bottomCardObserver = new IntersectionObserver(
        (entries, observer) => {
          if (entries[0].isIntersecting) {
            this.fetchMoreResults();
          }
        },
        {
          rootMargin: "0px 0px 0px 0px",
          threshold: 0,
        }
      );
      this.bottomCardObserver.observe(newBottomCard.$el);
    },
    onSelectedParshaChange(parshaSlug) {
      this.selectedParsha = parshaSlug;
      this.updateRouteIfNeeded();
    },
    onSelectedFiltersChange(filters) {
      // https://app.asana.com/0/1201959796113610/1201960012700461/f
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "search_filter",
          searchinfo: {
            year: filters.selectedYears.filter(
              (x) => !this.selectedYears.includes(x)
            ),
            series: filters.selectedSeries.filter(
              (x) => !this.selectedSeries.includes(x)
            ),
            category: filters.selectedCategories.filter(
              (x) => !this.selectedCategories.includes(x)
            )
          },
        });
      }

      this.selectedYears = filters.selectedYears.sort();
      this.selectedSeries = filters.selectedSeries.sort();
      this.selectedCategories = filters.selectedCategories.sort();
      this.updateRouteIfNeeded();
    },
    onSearchKeywordsChange(keywords) {
      // https://app.asana.com/0/1201959796113610/1201960012700461/f
      if (keywords != this.searchKeywords) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "search_filter",
            searchinfo: {
              keyword: keywords,
            },
          });
        }
      }

      this.searchKeywords = keywords;
      this.updateRouteIfNeeded();
    },
    onSelectedLanguageChange(language) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "search_filter",
          searchinfo: {
            language: language,
          },
        });
      }

      this.selectedLanguage = language;
      this.updateRouteIfNeeded();
    },
    updateRouteIfNeeded() {
      let newRouteString = "";
      if (this.selectedParsha) {
        newRouteString += this.selectedParsha;
        newRouteString += "/";
      }
      if (this.selectedYears.length) {
        this.selectedYears.forEach((element, i) => {
          if (i !== 0) newRouteString += ",";
          newRouteString += element;
        });
        newRouteString += "/";
      }
      if (this.selectedSeries.length) {
        this.selectedSeries.forEach((element, i) => {
          if (i !== 0) newRouteString += ",";
          newRouteString += element;
        });
        newRouteString += "/";
      }
      if (this.selectedCategories.length) {
        this.selectedCategories.forEach((element, i) => {
          if (i !== 0) newRouteString += ",";
          newRouteString += element;
        });
        newRouteString += "/";
      }
      if (this.searchKeywords) {
        newRouteString += "?keywords=" + encodeURIComponent(this.searchKeywords);
      }
      if (this.selectedLanguage !== 'en') {
        let _separator = '?';
        if (this.searchKeywords) {
          _separator = '&';
        }
        newRouteString += _separator + "lang=" + encodeURIComponent(this.selectedLanguage);
      }
      let currentRouteString = this.$route.fullPath.replace(this.baseURL, "");
      if (newRouteString != currentRouteString) {
        this.$router.push(this.baseURL + newRouteString);
      }
    },
    onRouteUpdated() {
      let updatedRoute = new URL(this.$route.fullPath, window.location);
      let folders = updatedRoute.pathname
        .replace(this.baseURL, "")
        .split("/")
        .join(",")
        .split(",")
        .filter((item) => item.length);
      let _selectedParsha = "";
      let _selectedYears = [];
      let _selectedSeries = [];
      let _selectedCategories = [];
      folders.forEach((folder) => {
        if (this.fetchedParshot.some((parsha) => parsha.slug === folder)) {
          _selectedParsha = folder;
          return;
        }
        if (this.fetchedYears.some((year) => year.slug === folder)) {
          _selectedYears.push(folder);
          return;
        }
        if (this.fetchedSeries.some((series) => series.slug === folder)) {
          _selectedSeries.push(folder);
          return;
        }
        if (
          this.fetchedCategories.some((category) => category.slug === folder)
        ) {
          _selectedCategories.push(folder);
          return;
        }
      });
      this.selectedParsha = _selectedParsha ? _selectedParsha : "";
      this.selectedYears = _selectedYears ? _selectedYears : [];
      this.selectedSeries = _selectedSeries ? _selectedSeries : [];
      this.selectedCategories = _selectedCategories ? _selectedCategories : [];
      this.searchKeywords = updatedRoute.searchParams.get("keywords")
        ? decodeURIComponent(updatedRoute.searchParams.get("keywords"))
        : "";
      this.selectedLanguage = updatedRoute.searchParams.get("lang")
        ? decodeURIComponent(updatedRoute.searchParams.get("lang"))
        : "en";
      this.fetchResults();
    },
    onScrollToTopRequest() {
      // window.scrollTo({
      //   top: this.$el.getBoundingClientRect().y - 60,
      //   left: 0,
      //   behavior: "smooth",
      // });
    },
  },
};
</script>
